import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { STORE_PATH, isLockedPath } from '../../lib/access';
import { useAccess } from './use-access';

const SHAKE_MS = 500;

/**
 * "Papel vegetal": in limited mode (trial over, nothing paid) the learning
 * pages render exactly as usual, then sit under one tracing-paper layer —
 * greyscale, dimmed, no pointer events, no keyboard focus (`inert`) — with a
 * single red chip pinned at the top that links to the store. A tap on the
 * faded area shakes the chip instead of toasting or redirecting.
 *
 * Wraps the shell `<Outlet/>`; pages never know about it. The store,
 * `/validate` and the dashboard stay fully usable (`isLockedPath`).
 */
export function LimitedVeil({ children }: { children: ReactNode }) {
  const { data } = useAccess();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const veiled = Boolean(data?.limited) && isLockedPath(pathname);
  if (!veiled) return <>{children}</>;
  return <Veil>{children}</Veil>;
}

function Veil({ children }: { children: ReactNode }) {
  const { t } = useTranslation('p2e');
  const [shaking, setShaking] = useState(false);
  const timer = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // `inert` is not in React's typed attributes everywhere; set it imperatively
  // so browsers without it still get aria-hidden and no focusable children.
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    el.setAttribute('inert', '');
    el.setAttribute('aria-hidden', 'true');
    if (!('inert' in HTMLElement.prototype)) {
      el.querySelectorAll<HTMLElement>('a, button, input, select, textarea, [tabindex]').forEach((node) => {
        node.setAttribute('tabindex', '-1');
      });
    }
  }, []);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const shake = useCallback(() => {
    setShaking(true);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShaking(false), SHAKE_MS);
  }, []);

  return (
    <div data-testid="limited-veil" className="relative" onClick={shake}>
      <style>{`
        @keyframes p2e-veil-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .p2e-veil-chip[data-shaking="true"] { animation: p2e-veil-shake ${SHAKE_MS}ms ease-in-out; }
        @media (prefers-reduced-motion: reduce) { .p2e-veil-chip[data-shaking="true"] { animation: none; } }
      `}</style>
      <div className="sticky top-3 z-20 mb-3 flex justify-center">
        <Link
          to={STORE_PATH}
          data-testid="limited-veil-chip"
          data-shaking={shaking ? 'true' : 'false'}
          onClick={(event) => event.stopPropagation()}
          className="p2e-veil-chip inline-flex items-center gap-2 rounded-full bg-[#D41920] px-4 py-2 font-['Oswald'] text-[11px] font-bold uppercase tracking-[0.08em] text-white shadow-lg hover:bg-[#b8151b]"
        >
          <Lock size={14} aria-hidden />
          {t('access.veil.chip')}
        </Link>
      </div>
      <div
        ref={contentRef}
        data-testid="limited-veil-content"
        className="select-none"
        style={{ filter: 'grayscale(1)', opacity: 0.55, pointerEvents: 'none' }}
      >
        {children}
      </div>
    </div>
  );
}
